import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getAuth } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functionsEast } from "../../firebase";
import {
  FiMessageCircle,
  FiSend,
  FiX,
  FiUser,
  FiCpu,
  FiAlertCircle,
  FiMinimize2,
  FiMic,
  FiUpload,
  FiUsers,
  FiZap,
  FiHelpCircle,
} from "react-icons/fi";

const FloatingAIChat = ({ businessId: propBusinessId }) => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const auth = getAuth();

  // Chat state
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [businessId, setBusinessId] = useState(propBusinessId || null);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [isNewBusiness, setIsNewBusiness] = useState(false);
  const [adminFirstName, setAdminFirstName] = useState("");
  const recognitionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const hasAutoOpened = useRef(false);
  const pendingSendRef = useRef(null);

  // Draggable button state. `position` is { x, y } in viewport pixels for the
  // button's top-left corner. `null` means default bottom-right anchor.
  const BUTTON_SIZE = 56;
  const DRAG_THRESHOLD = 5;
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem("floatingAIChat.position");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const dragState = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    origX: 0,
    origY: 0,
    moved: false,
  });
  const buttonRef = useRef(null);

  // Keep button inside viewport if the window resizes smaller than its position.
  useEffect(() => {
    if (!position) return;
    const onResize = () => {
      setPosition((p) => {
        if (!p) return p;
        const maxX = window.innerWidth - BUTTON_SIZE - 8;
        const maxY = window.innerHeight - BUTTON_SIZE - 8;
        const clampedX = Math.max(8, Math.min(maxX, p.x));
        const clampedY = Math.max(8, Math.min(maxY, p.y));
        return clampedX === p.x && clampedY === p.y ? p : { x: clampedX, y: clampedY };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [position]);

  const handlePointerDown = (e) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left,
      origY: rect.top,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (dragState.current.pointerId !== e.pointerId) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (!dragState.current.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      dragState.current.moved = true;
    }
    if (dragState.current.moved) {
      const maxX = window.innerWidth - BUTTON_SIZE - 8;
      const maxY = window.innerHeight - BUTTON_SIZE - 8;
      const x = Math.max(8, Math.min(maxX, dragState.current.origX + dx));
      const y = Math.max(8, Math.min(maxY, dragState.current.origY + dy));
      setPosition({ x, y });
    }
  };

  const handlePointerUp = (e) => {
    if (dragState.current.pointerId !== e.pointerId) return;
    if (dragState.current.moved) {
      try {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) {
          localStorage.setItem(
            "floatingAIChat.position",
            JSON.stringify({ x: rect.left, y: rect.top })
          );
        }
      } catch {
        // ignore storage errors
      }
    }
    dragState.current.pointerId = null;
  };

  const handleButtonClick = () => {
    // If the pointer moved past the threshold, this was a drag — swallow the click.
    if (dragState.current.moved) {
      dragState.current.moved = false;
      return;
    }
    toggleChat();
  };

  // Inline style for the button when dragged (overrides default bottom-6 right-6).
  const buttonStyle = position ? { left: position.x, top: position.y } : undefined;

  // Compute chat-window position relative to the button, keeping it on-screen.
  const windowStyle = (() => {
    if (!position) return undefined;
    const windowWidth = isMinimized ? 288 : 420;
    const windowHeight = isMinimized ? 120 : 550;
    const gap = 12;
    const buttonCenterX = position.x + BUTTON_SIZE / 2;
    const buttonCenterY = position.y + BUTTON_SIZE / 2;
    const preferAbove = buttonCenterY > window.innerHeight / 2;
    const preferLeftAligned = buttonCenterX < window.innerWidth / 2;

    let left = preferLeftAligned ? position.x : position.x + BUTTON_SIZE - windowWidth;
    let top = preferAbove
      ? position.y - windowHeight - gap
      : position.y + BUTTON_SIZE + gap;

    left = Math.max(8, Math.min(window.innerWidth - windowWidth - 8, left));
    top = Math.max(8, Math.min(window.innerHeight - windowHeight - 8, top));
    return { left, top };
  })();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Track current language for detecting changes
  const currentLanguage = i18n.language;
  const previousLanguageRef = useRef(currentLanguage);

  // Initialize welcome message when chat opens for first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeText = isNewBusiness && adminFirstName
        ? t('settings:support.chatbot.onboarding.greeting', { name: adminFirstName })
        : t('settings:support.chatbot.welcomeMessage');
      setMessages([
        {
          id: 1,
          type: "bot",
          text: welcomeText,
          isOnboarding: isNewBusiness
        }
      ]);
    }
  }, [isOpen, messages.length, t, isNewBusiness, adminFirstName]);

  // Update welcome message when language changes
  useEffect(() => {
    if (previousLanguageRef.current !== currentLanguage && messages.length > 0) {
      // Update only the welcome message (first message) if it's a bot message
      setMessages(prevMessages => {
        if (prevMessages.length > 0 && prevMessages[0].type === "bot" && prevMessages[0].id === 1) {
          return [
            {
              ...prevMessages[0],
              text: t('settings:support.chatbot.welcomeMessage')
            },
            ...prevMessages.slice(1)
          ];
        }
        return prevMessages;
      });
      previousLanguageRef.current = currentLanguage;
    }
  }, [currentLanguage, t, messages.length]);

  // Fetch businessId, admin name, and onboarding status on mount
  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) {
        setIsInitialized(true);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.data();
        const bizId = propBusinessId || userData?.businessId;
        setBusinessId(bizId);

        // Get admin first name
        const fullName = userData?.name || user.displayName || "";
        setAdminFirstName(fullName.split(" ")[0]);

        // Check if business has completed onboarding
        if (bizId) {
          const bizDoc = await getDoc(doc(db, "businesses", bizId));
          const bizData = bizDoc.data();
          if (bizData && !bizData.onboardingCompleted) {
            setIsNewBusiness(true);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
      setIsInitialized(true);
    };

    fetchData();
  }, [auth, propBusinessId]);

  // Auto-open chat for new businesses
  useEffect(() => {
    if (isNewBusiness && isInitialized && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      setIsOpen(true);
    }
  }, [isNewBusiness, isInitialized]);

  // Reset unread count when chat opens
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    const userText = input.trim();
    const userMessage = {
      id: Date.now(),
      type: "user",
      text: userText
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);
    setError(null);

    // Build conversation history for Claude
    const newHistory = [
      ...conversationHistory,
      { role: "user", content: userText }
    ];

    try {
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error("not_authenticated");
      }

      // Call the AI assistant function
      const chatWithAssistant = httpsCallable(functionsEast, "chatWithAssistant");
      const result = await chatWithAssistant({
        messages: newHistory,
        businessId: businessId,
        language: i18n.language || 'en'
      });

      const aiResponse = result.data.message;

      // Update conversation history
      setConversationHistory([
        ...newHistory,
        { role: "assistant", content: aiResponse }
      ]);

      const botResponse = {
        id: Date.now() + 1,
        type: "bot",
        text: aiResponse
      };

      setMessages(prev => [...prev, botResponse]);

      // Increment unread count if chat is minimized
      if (isMinimized) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (err) {
      console.error("AI Chat error:", err);

      let errorMessage = t('settings:support.chatbot.errorGeneric');

      if (err.message === "not_authenticated") {
        errorMessage = t('settings:support.chatbot.errorNotAuthenticated');
      } else if (err.code === "functions/resource-exhausted") {
        errorMessage = t('settings:support.chatbot.errorCreditsExhausted');
      } else if (err.code === "functions/unavailable") {
        errorMessage = t('settings:support.chatbot.errorUnavailable');
      }

      setError(errorMessage);

      const errorResponse = {
        id: Date.now() + 1,
        type: "bot",
        text: errorMessage,
        isError: true
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleVoice = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError(t('settings:support.chatbot.voiceNotSupported'));
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    const langMap = { en: 'en-US', es: 'es-ES', pt: 'pt-BR' };
    recognition.lang = langMap[i18n.language] || 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setInput(transcript);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === 'not-allowed') {
        setError(t('settings:support.chatbot.voicePermissionDenied'));
      }
    };

    recognition.start();
    setIsListening(true);
  };

  const clearConversation = () => {
    setMessages([
      {
        id: Date.now(),
        type: "bot",
        text: t('settings:support.chatbot.welcomeMessage')
      }
    ]);
    setConversationHistory([]);
    setError(null);
  };

  const toggleChat = () => {
    if (isMinimized) {
      setIsMinimized(false);
      setUnreadCount(0);
    } else if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  const minimizeChat = () => {
    setIsMinimized(true);
  };

  const closeChat = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  // Auto-send when triggered by onboarding action
  useEffect(() => {
    if (pendingSendRef.current && input === pendingSendRef.current) {
      pendingSendRef.current = null;
      handleSend();
    }
  }, [input]);

  const handleOnboardingAction = (action) => {
    if (action === "ask") {
      return;
    }
    const promptKey = {
      import: 'settings:support.chatbot.onboarding.importDataPrompt',
      invite: 'settings:support.chatbot.onboarding.inviteTeamPrompt',
      meet: 'settings:support.chatbot.onboarding.meetAIPrompt',
    }[action];
    if (promptKey) {
      const text = t(promptKey);
      pendingSendRef.current = text;
      setInput(text);
    }
  };

  const onboardingActions = [
    { key: "import", icon: FiUpload, label: t('settings:support.chatbot.onboarding.importData') },
    { key: "invite", icon: FiUsers, label: t('settings:support.chatbot.onboarding.inviteTeam') },
    { key: "meet", icon: FiZap, label: t('settings:support.chatbot.onboarding.meetAI') },
    { key: "ask", icon: FiHelpCircle, label: t('settings:support.chatbot.onboarding.askSomething') },
  ];

  const suggestedQuestions = [
    t('settings:support.chatbot.suggestedQuestions.createInvoice'),
    t('settings:support.chatbot.suggestedQuestions.employeeTasks'),
    t('settings:support.chatbot.suggestedQuestions.paymentMethods'),
    t('settings:support.chatbot.suggestedQuestions.trackExpenses')
  ];

  // Don't render if user is not authenticated
  if (!auth.currentUser) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{ ...buttonStyle, touchAction: "none" }}
        className={`${position ? "fixed" : "fixed bottom-6 right-6"} z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-shadow duration-200 hover:scale-110 cursor-grab active:cursor-grabbing ${
          isOpen && !isMinimized
            ? "bg-slate-600 hover:bg-slate-700"
            : "bg-gradient-to-r from-emerald-500 via-sky-500 to-blue-500 hover:shadow-xl"
        }`}
        aria-label="Toggle AI Chat"
      >
        {isOpen && !isMinimized ? (
          <FiX className="w-6 h-6 text-white" />
        ) : (
          <>
            <FiMessageCircle className="w-6 h-6 text-white" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          style={windowStyle}
          className={`fixed z-50 transition-all duration-300 ${
            windowStyle
              ? isMinimized
                ? "w-72"
                : "w-[380px] sm:w-[420px]"
              : isMinimized
                ? "bottom-24 right-6 w-72"
                : "bottom-24 right-6 w-[380px] sm:w-[420px]"
          }`}
        >
          <div className={`bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col ${
            isMinimized ? "h-auto" : "h-[500px] sm:h-[550px]"
          }`}>
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-emerald-500 via-sky-500 to-blue-500 p-3 text-white flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <FiCpu className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{t('settings:support.chatbot.title')}</h3>
                    {!isMinimized && (
                      <p className="text-xs text-white/80">{t('settings:support.chatbot.subtitle')}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {conversationHistory.length > 0 && !isMinimized && (
                    <button
                      onClick={clearConversation}
                      className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                      title={t('settings:support.chatbot.clearConversation')}
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={isMinimized ? () => setIsMinimized(false) : minimizeChat}
                    className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                    title={isMinimized ? "Expand" : "Minimize"}
                  >
                    <FiMinimize2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={closeChat}
                    className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                    title="Close"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Minimized state - just show header */}
            {isMinimized ? (
              <div
                className="p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setIsMinimized(false)}
              >
                <p className="text-sm text-slate-600 truncate">
                  {messages.length > 0 ? messages[messages.length - 1].text.substring(0, 50) + "..." : t('settings:support.chatbot.welcomeMessage').substring(0, 50) + "..."}
                </p>
              </div>
            ) : (
              <>
                {/* Messages Container */}
                <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`flex gap-2 max-w-[85%] ${msg.type === "user" ? "flex-row-reverse" : ""}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                          msg.type === "user"
                            ? "bg-gradient-to-r from-emerald-500 to-sky-500"
                            : msg.isError
                              ? "bg-red-500"
                              : "bg-gradient-to-r from-sky-500 to-blue-500"
                        }`}>
                          {msg.type === "user" ? (
                            <FiUser className="w-3.5 h-3.5 text-white" />
                          ) : msg.isError ? (
                            <FiAlertCircle className="w-3.5 h-3.5 text-white" />
                          ) : (
                            <FiCpu className="w-3.5 h-3.5 text-white" />
                          )}
                        </div>
                        <div className={`px-3 py-2 rounded-2xl ${
                          msg.type === "user"
                            ? "bg-gradient-to-r from-emerald-500 to-sky-500 text-white"
                            : msg.isError
                              ? "bg-red-50 border border-red-200 text-red-700"
                              : "bg-white border border-slate-200 text-slate-700"
                        }`}>
                          <p className="whitespace-pre-line text-sm">{msg.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="flex gap-2 max-w-[85%]">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-sky-500 to-blue-500 flex items-center justify-center">
                          <FiCpu className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="px-3 py-2 rounded-2xl bg-white border border-slate-200">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Onboarding Actions or Suggested Questions */}
                {messages.length === 1 && (
                  <div className="px-3 py-2 bg-white border-t border-slate-100 flex-shrink-0">
                    {isNewBusiness ? (
                      <div className="grid grid-cols-2 gap-2">
                        {onboardingActions.map((action) => {
                          const Icon = action.icon;
                          return (
                            <button
                              key={action.key}
                              onClick={() => handleOnboardingAction(action.key)}
                              className="flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-slate-50 to-sky-50 border border-slate-200 rounded-xl hover:from-emerald-50 hover:to-sky-50 hover:border-emerald-300 transition-all text-left group"
                            >
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-emerald-500 to-sky-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                <Icon className="w-4 h-4 text-white" />
                              </div>
                              <span className="text-xs font-medium text-slate-700">{action.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-slate-500 mb-1.5">{t('settings:support.chatbot.suggestedLabel')}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {suggestedQuestions.map((q, idx) => (
                            <button
                              key={idx}
                              onClick={() => setInput(q)}
                              className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full hover:bg-emerald-100 transition-colors"
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Input Area */}
                <div className="p-3 bg-white border-t border-slate-200 flex-shrink-0">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={isListening ? t('settings:support.chatbot.voiceListening') : isTyping ? t('settings:support.chatbot.thinkingPlaceholder') : t('settings:support.chatbot.inputPlaceholder')}
                      disabled={isTyping || !isInitialized}
                      className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 transition-colors disabled:bg-slate-50 disabled:cursor-not-allowed"
                    />
                    <button
                      onClick={toggleVoice}
                      disabled={isTyping || !isInitialized}
                      className={`px-3 py-2 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        isListening
                          ? "bg-red-500 text-white animate-pulse"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                      aria-label="Voice input"
                    >
                      <FiMic className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleSend}
                      disabled={!input.trim() || isTyping || !isInitialized}
                      className="px-3 py-2 bg-gradient-to-r from-emerald-500 to-sky-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiSend className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingAIChat;
